import { fixedUi } from '@/i18n/fixedUi';
import React, { useRef, useState } from 'react';
import SignedImage from '@/components/ipe/SignedImage';
import TiktokImagePreviewLightbox from '@/tiktok-platform/modules/products/components/TiktokImagePreviewLightbox';

/**
 * TikTok CDN / 公网绝对地址可直接展示；IPE 受管存储走 SignedImage。
 * 本地 uploads（含跨端口 localhost:2006）也走直链，避免 SignedImage 误伤草稿箱缩略图。
 */
export function isDirectDisplayImageUrl(url) {
  const u = String(url || '').trim();
  if (!u) return false;
  if (u.startsWith('data:') || u.startsWith('blob:')) return true;
  const normalized = u.startsWith('//')
    ? `https:${u}`
    : (/^(img\.|.*ltwebstatic|.*ltwebimg|.*tiktok\.com|.*imgdeal)/i.test(u) && !/^https?:\/\//i.test(u)
      ? `https://${u.replace(/^\/+/, '')}`
      : u);
  if (!/^https?:\/\//i.test(normalized)) return false;
  try {
    const parsed = new URL(normalized, typeof window !== 'undefined' ? window.location.origin : 'https://local');
    const host = parsed.hostname || '';
    const path = parsed.pathname || '';
    if (/\/uploads\//i.test(path) || host === 'localhost' || host === '127.0.0.1') {
      return true;
    }
    if (/tiktok|ltwebstatic|ltwebimg|alicdn|qiniucdn|cloudfront|imgdeal/i.test(host)) {
      return true;
    }
    if (typeof window !== 'undefined' && host === window.location.hostname && /\/api\//i.test(path)) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * 浏览器缩略图是否大概率能打开。
 * 线下 TikTok 测试 CDN（imgdeal-test01 等）常 403/裂图，预览应避开。
 */
export function isLikelyBrowserReachableThumb(url) {
  const u = String(url || '').trim().toLowerCase();
  if (!u) return false;
  if (u.startsWith('data:') || u.startsWith('blob:')) return true;
  if (u.includes('localhost') || u.includes('127.0.0.1') || u.includes('/uploads/')) return true;
  if (u.includes('imgdeal-test') || u.includes('tiktokcorp.cn') || u.includes('-test01.')) return false;
  // 外包装 imageBizType=1 返回的 private-tag 链浏览器常 403，预览必须走 ipeUrl/blob
  if (u.includes('private-tag') || u.includes('pfs.file.tiktok') || u.includes('kjpfs')) return false;
  return /^https?:\/\//.test(u) || u.startsWith('//');
}

/** 多候选地址中挑浏览器可预览的；提交仍应用 tiktokUrl。 */
export function pickBrowserPreviewImageUrl(...candidates) {
  const list = candidates.map((u) => String(u || '').trim()).filter(Boolean);
  const reachable = list.find((u) => isLikelyBrowserReachableThumb(u));
  return reachable || list[0] || '';
}

/**
 * 发布记录 / 草稿箱 / 产品信息 SKC 缩略图；默认点击可大图预览。
 */
export default function TiktokProductThumb({
  imageUrl,
  className = 'tiktok-product-spec__img',
  placeholderClassName = 'tiktok-product-spec__img tiktok-product-spec__img--placeholder',
  alt = '',
  enablePreview = true,
}) {
  const [failed, setFailed] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');
  const imgRef = useRef(null);
  const rawUrl = String(imageUrl || '').trim();
  const url = rawUrl.startsWith('//')
    ? `https:${rawUrl}`
    : (/^(img\.|.*ltwebstatic|.*ltwebimg|.*tiktok\.com|.*imgdeal)/i.test(rawUrl) && !/^https?:\/\//i.test(rawUrl)
      ? `https://${rawUrl.replace(/^\/+/, '')}`
      : rawUrl);

  if (!url || failed) {
    return <span className={placeholderClassName}>—</span>;
  }

  const openPreview = (e) => {
    if (!enablePreview) return;
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const fromDom = imgRef.current?.currentSrc || imgRef.current?.src || '';
    const next = String(fromDom || url).trim();
    if (next) setPreviewSrc(next);
  };

  const thumbClassName = enablePreview
    ? `${className} tiktok-product-spec__img--clickable`.trim()
    : className;

  const media = isDirectDisplayImageUrl(url) ? (
    <img
      ref={imgRef}
      src={url}
      alt={alt}
      className={thumbClassName}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  ) : (
    <SignedImage
      ref={imgRef}
      url={url}
      alt={alt}
      className={thumbClassName}
      fallback={<span className={placeholderClassName}>—</span>}
    />
  );

  return (
    <>
      {enablePreview ? (
        <button
          type="button"
          className="tiktok-product-spec__img-btn"
          onClick={openPreview}
          title={fixedUi("点击预览大图")}
          aria-label={fixedUi("预览大图")}
        >
          {media}
        </button>
      ) : (
        media
      )}
      {enablePreview && previewSrc ? (
        <TiktokImagePreviewLightbox
          src={previewSrc}
          alt={alt || 'SKC 预览'}
          onClose={() => setPreviewSrc('')}
        />
      ) : null}
    </>
  );
}
